/**
 * 开发模式：给每个板块/元素一个可复制的 code，用于「改哪里就把 code 发给 AI」。
 *
 * 目的：用户不必再向 AI 描述“哪个页面哪个板块哪个位置”，直接把 code 复制给 AI。
 * AI 拿到 `inv.table` 即可精确定位到板块与元素。
 *
 * 本文件是零框架依赖的纯 TS（不 import Vue / Pinia / 本项目任何模块），因此可以单跑单测，
 * 也可以被任何 UI 层（这里由 `useDevMode.ts` 这个 Vue 适配层包一层）调用。
 *
 * ## 两条铁律（与 kucun-kankan 同源，本项目照搬思路）
 *  1. **生产绝不可见**。环境判定只认后端 `/api/version` 返回的 `env` 字段
 *     （后端用 `isLocalEnv()` 判定，比前端猜 IP 可靠），且失败一律视为 prod。
 *     顶栏那个开关按钮也只在探测到 local 后才渲染（AppShell 里另有一道同源判定）。
 *  2. **关闭时零开销**。关闭状态下不挂任何监听、不注入任何图标、不参与渲染。
 *
 * ## code 推导分两层
 *  - 显式标注：元素（或其祖先）带 data-dev="inv.table" → 直接用。
 *  - 自动推导：显式标注内部的具体元素按结构补后缀（表 → .table，表头 → .table.th:列名，
 *    单元格 → .table.td:列名 或 .table.td:c{n}，按钮 → .btn:文案），页签 → tb.tab.{key}。
 */

const STORAGE_KEY = 'skuDevMode'
const TOGGLE_KEYS = { key: 'd', ctrl: true, shift: true }
/** 鼠标离开图标/提示到隐藏的宽限时间：给「从图标移到提示上的复制按钮」留时间。 */
const HIDE_GRACE_MS = 220
/** 注入图标的标记类：用于扫描、清理、以及「已是当前节点」的判定。 */
const PIN_CLASS = 'dm-pin'
const PIN_HOST_ATTR = 'data-dm-pin'

interface Hint {
  code: string
  /** 板块级锚点（不含自动推导后缀），便于 AI 快速定位到"改哪个板块"。 */
  base: string
  /** 元素角色说明（如「表头 · SKU」「按钮」「KPI 卡」），比源文件路径可靠。 */
  role: string
  target: HTMLElement
}

let enabled = false
let envChecked = false
let isLocalEnv = false
let hostEl: HTMLDivElement | null = null
let boxEl: HTMLDivElement | null = null
let hintEl: HTMLDivElement | null = null
let current: Hint | null = null
let copyTimer = 0
let hideTimer = 0
/** Alt 细粒度模式：仅在按住 Alt 时才做「跟随悬停的逐元素推导」。 */
let altHeld = false
/** 已注入图标的节点。⚠️ 必须是 `let` 且关闭时**整体换新**——
 *  它是 WeakSet，没有 clear()；若只删 DOM 不清这里，重新开启时
 *  `pinNode()` 会以为"已注入过"而直接 return，结果图标一个都回不来。
 *  （关→开用例实测抓到，之前只测过单向关闭所以没暴露。） */
let pinned = new WeakSet<HTMLElement>()
/** 记录注入的图标 DOM，关闭时统一移除。 */
const pinEls = new Set<HTMLElement>()
let pinScanTimer = 0

/* ------------------------------------------------- 顶栏开关按钮的对外接口
 * 开关状态与 Alt 状态要能让 Vue 响应式读到（按钮高亮 / 提示文案），
 * 所以这里用订阅回调推给 Vue，而不是让 Vue 去轮询 DOM 的 class。
 * 不用 Vue 的 ref 是为了保持本模块零框架依赖（测试里可单独跑）。 */

export type DevModeState = {
  /** 开发模式是否开启（板块是否注入 ⧉ 图标） */
  enabled: boolean
  /** 是否按住 Alt（细粒度模式） */
  altHeld: boolean
  /** 后端是否判定为本地/测试环境。**顶栏按钮靠它决定渲染与否** */
  local: boolean
}
type DevModeListener = (s: DevModeState) => void
const listeners = new Set<DevModeListener>()

function emit(): void {
  const snap: DevModeState = { enabled, altHeld, local: isLocalEnv }
  for (const fn of listeners) {
    try { fn(snap) } catch { /* 单个订阅者出错不影响其他 */ }
  }
}

/** 订阅开发模式状态（返回退订函数）。回调会**立即被调用一次**推送当前值，
 *  所以订阅方不需要自己初始化、也不需要自己去探测环境（避免重复打接口）。 */
export function onDevModeChange(fn: DevModeListener): () => void {
  listeners.add(fn)
  try { fn({ enabled, altHeld, local: isLocalEnv }) } catch { /* 忽略 */ }
  return () => { listeners.delete(fn) }
}

/** 顶栏按钮点击：切换开发模式（等价于 Ctrl+Shift+D，且会记住用户选择）。
 *  返回是否真的改变了状态——非本地/测试环境会被拒绝，调用方据此给提示。 */
export function toggleDevMode(): boolean {
  const want = !enabled
  setEnabled(want, true)
  return enabled === want
}

/** 探测当前是否本地/测试环境（后端 env==='local'）。供顶栏决定按钮是否渲染。 */
export function isDevEnv(): Promise<boolean> {
  return detectLocalEnv()
}

/* ------------------------------------------------------------------ 环境探测 */

/** 后端是唯一裁判：env==='local' 才允许开发模式存在。任何异常一律当生产。
 *  注意：失败**不缓存**结论——页面刚加载时接口可能瞬时不可用，
 *  若把失败当"生产"记死，就会永久禁用开发模式（踩过）。 */
async function detectLocalEnv(): Promise<boolean> {
  if (envChecked) return isLocalEnv
  try {
    // 与 useVersionGuard 共用同一个 /api/version 接口，但各取所需：它读 version，本模块只读 env。
    // 两者都是各自模块内部状态，不会互相覆盖（见本仓库 AGENTS.md 开发模式一节）。
    const r = await fetch('/api/version', { cache: 'no-store' }).then((x) => x.json())
    isLocalEnv = r?.env === 'local'
    envChecked = true          // 只在拿到明确结果时才锁定
  } catch {
    isLocalEnv = false         // 本次按生产处理，但下次调用仍会重试
  }
  emit()   // 结论出来后推给订阅方（顶栏按钮据此出现/消失）
  return isLocalEnv
}

/* ------------------------------------------------------------- code 推导规则 */

function norm(s: string): string {
  return s.replace(/\s+/g, ' ').trim().slice(0, 24)
}

/** 沿 DOM 向上找最近的显式标注（含自身）。 */
function findAnchor(el: HTMLElement | null): { el: HTMLElement; code: string } | null {
  for (let cur = el; cur; cur = cur.parentElement) {
    const code = cur.dataset ? cur.dataset.dev : undefined
    if (code) return { el: cur, code }
  }
  return null
}

/** 从 section 锚点到具体元素，推导出可定位的 code 与人类可读的角色说明。 */
function deriveCode(target: HTMLElement, section: HTMLElement, base: string): { code: string; role: string } {
  if (target === section) return { code: base, role: '板块容器' }

  // 页签：顶栏 tab 与“更多”菜单里的 tab 都归到 tb.tab.<key>
  const tabKey = target.dataset.devTab
  if (tabKey) return { code: `tb.tab.${tabKey}`, role: '页签' }

  // 表格内部：表头带列名，单元格归到列，其余归到表
  const table = target.closest('table')
  if (table) {
    const th = target.closest('th')
    if (th) {
      const label = norm(th.textContent || '')
      return {
        code: label ? `${base}.table.th:${label}` : `${base}.table.th`,
        role: label ? `表头 · ${label}` : '表头',
      }
    }
    const td = target.closest('td')
    if (td) {
      const head = td.closest('tr')?.parentElement?.querySelectorAll('th')
      const idx = Array.from((td.parentElement as HTMLTableRowElement | null)?.cells || []).indexOf(td as HTMLTableCellElement)
      const label = head && idx >= 0 ? norm(head[idx]?.textContent || '') : ''
      // 表头可能缺失或该列无文字（如首列的操作按钮/序号）——用列序号兜底，
      // 否则同一张表里所有无名单元格会退化成同一个 code，失去区分度。
      return {
        code: label ? `${base}.table.td:${label}` : `${base}.table.td:c${idx + 1}`,
        role: label ? `单元格 · ${label}` : `单元格 · 第${idx + 1}列`,
      }
    }
    return { code: `${base}.table`, role: '表格' }
  }

  // 整块可点击卡片（KPI 卡 / 指标卡）：直接用板块 code——卡内任意位置（标签、数值、副栏）
  // 都指向同一张卡，再去拼 .btn: 只会得到「万元(¥)」这类无意义后缀。
  if (target.closest('.kpi') || target.closest('.metric-card')) return { code: base, role: '指标卡' }
  if (target.closest('.kpi-age') || target.closest('.dash-card')) return { code: base, role: '区块卡' }

  // 按钮：取自身的短标签（优先 .lb 这类语义标签），不要整卡文本——否则 code 会长到没法用
  const btn = target.closest('button, .btn, .fchip, .ms-item')
  if (btn) {
    const label = norm(btn.querySelector('.lb, .fc-pt, b')?.textContent || btn.textContent || '')
    return { code: label ? `${base}.btn:${label}` : `${base}.btn`, role: label ? `按钮 · ${label}` : '按钮' }
  }

  // 其余：用 tagName + class 首段兜底，避免全退化成 section 本身
  const cls = String(target.className || '').split(/\s+/).find((c) => c && !c.startsWith('ng-'))
  return {
    code: cls ? `${base}.${target.tagName.toLowerCase()}.${cls.slice(0, 16)}` : `${base}.${target.tagName.toLowerCase()}`,
    role: target.tagName.toLowerCase(),
  }
}

/** 板块锚点上的补充说明（可选，用 data-dev-src 标注，如「图表配置」）。
 *  注意：Vue 的 __file 只在 dev server 下存在，生产构建里拿不到——
 *  本地跑的是 build 产物，所以不能依赖它，改用显式标注。 */
function sourceNote(section: HTMLElement): string {
  return section.dataset.devSrc || ''
}

function resolveHint(target: HTMLElement): Hint | null {
  // 自带 code 的元素（页签 data-dev-tab、显式 data-dev）：直接用它，不必向上找
  const ownTab = target.closest('[data-dev-tab]') as HTMLElement | null
  if (ownTab) {
    return { code: `tb.tab.${ownTab.dataset.devTab}`, base: `tb.tab.${ownTab.dataset.devTab}`, role: '页签', target }
  }
  const own = target.closest('[data-dev]') as HTMLElement | null
  if (own) {
    const base = own.dataset.dev as string
    const d = deriveCode(target, own, base)
    return { code: d.code, base, role: [d.role, sourceNote(own)].filter(Boolean).join(' · '), target }
  }
  // 没有显式标注：向上找最近的锚点，再按结构推导
  const anchor = findAnchor(target)
  if (!anchor) return null
  const d = deriveCode(target, anchor.el, anchor.code)
  return {
    code: d.code,
    base: anchor.code,
    role: [d.role, sourceNote(anchor.el)].filter(Boolean).join(' · '),
    target,
  }
}

/* ------------------------------------------------------- 常驻复制图标（主交互） */

/** 给一个可标注节点生成 code（节点自身即锚点）。 */
function nodeHint(el: HTMLElement): Hint | null {
  const tabKey = el.dataset.devTab
  if (tabKey) return { code: `tb.tab.${tabKey}`, base: `tb.tab.${tabKey}`, role: '页签', target: el }
  const code = el.dataset.dev
  if (!code) return null
  return { code, base: code, role: sourceNote(el) || '板块', target: el }
}

function makeIcon(): SVGSVGElement {
  // 两张错位圆角矩形 = 通用复制图标。内联 SVG，不引图标库。
  const NS = 'http://www.w3.org/2000/svg'
  const svg = document.createElementNS(NS, 'svg')
  svg.setAttribute('viewBox', '0 0 16 16')
  svg.setAttribute('width', '13')
  svg.setAttribute('height', '13')
  svg.setAttribute('aria-hidden', 'true')
  const r1 = document.createElementNS(NS, 'rect')
  r1.setAttribute('x', '5.5'); r1.setAttribute('y', '1.5')
  r1.setAttribute('width', '9'); r1.setAttribute('height', '11')
  r1.setAttribute('rx', '1.8')
  r1.setAttribute('fill', 'none')
  r1.setAttribute('stroke', 'currentColor')
  r1.setAttribute('stroke-width', '1.5')
  const r2 = document.createElementNS(NS, 'rect')
  r2.setAttribute('x', '1.5'); r2.setAttribute('y', '4.5')
  r2.setAttribute('width', '9'); r2.setAttribute('height', '10')
  r2.setAttribute('rx', '1.8')
  r2.setAttribute('fill', 'var(--dm-pin-bg, #1f2430)')
  r2.setAttribute('stroke', 'currentColor')
  r2.setAttribute('stroke-width', '1.5')
  svg.append(r1, r2)
  return svg
}

/** 往节点里注入固定复制图标（幂等）。 */
function pinNode(el: HTMLElement): void {
  // ★ v-html 宿主节点（下钻浮层/抽屉/弹窗里的表格区块）在 Vue 更新内容时会被整体
  //   替换 innerHTML —— 之前注入的 ⧉ 图标跟着被抹掉，但 pinned（WeakSet）还记着
  //   "已注入"，重扫时直接 return，图标永远回不来（实测：下钻后多个板块图标消失）。
  //   ECharts 容器同理：chart.init() 会清空容器再画。
  //   修法：不只看 pinned 记录，还要确认图标真的还挂在节点上；不在就补注入。
  // 用 children 判断（不依赖 :scope 选择器，部分 DOM 环境/happy-dom 不认 :scope）
  if (pinned.has(el) && Array.from(el.children).some((c) => c.classList.contains(PIN_CLASS))) return
  pinned.delete(el)
  const hint = nodeHint(el)
  if (!hint) return
  if (getComputedStyle(el).position === 'static') el.style.position = 'relative'

  const bt = document.createElement('button')
  bt.type = 'button'
  bt.className = PIN_CLASS
  bt.setAttribute('data-dm-code', hint.code)
  bt.title = `${hint.code}  ·  ${hint.role}　（点击复制）`
  bt.setAttribute('aria-label', `复制 ${hint.code}`)
  bt.appendChild(makeIcon())
  bt.addEventListener('click', (ev) => {
    ev.preventDefault()
    ev.stopPropagation()
    void copy(hint.code)
    flashPin(bt)
    showPinTip(bt, hint)
  })
  // 悬停图标 → 显示完整 code（固定位置，不追鼠标；鼠标离开给宽限）
  bt.addEventListener('pointerenter', () => {
    cancelHide()
    showPinTip(bt, hint)
  })
  bt.addEventListener('pointerleave', () => scheduleHide())
  el.appendChild(bt)
  pinned.add(el)
  pinEls.add(bt)
  // 顺带清理已被 v-html 抹掉、脱离 DOM 的旧图标引用（pinEls 只增不减会慢慢膨胀）
  for (const old of pinEls) {
    if (old.isConnected === false) pinEls.delete(old)
  }
}

/** 扫描当前 DOM，注入所有缺失的图标。页面切换/异步加载后需重扫。 */
function scanAndPin(): void {
  if (!enabled) return
  const nodes = document.querySelectorAll<HTMLElement>('[data-dev], [data-dev-tab]')
  nodes.forEach((el) => pinNode(el))
}

/** 重扫节流：Vue 异步渲染很多次，没必要每次都全量扫。 */
function scheduleScan(): void {
  window.clearTimeout(pinScanTimer)
  pinScanTimer = window.setTimeout(scanAndPin, 120)
}

/** 用 MutationObserver 跟踪异步渲染出的新板块（Vue 换页/数据到达后才挂 DOM）。 */
let mo: MutationObserver | null = null

function startObserve(): void {
  if (mo) return
  mo = new MutationObserver(() => scheduleScan())
  mo.observe(document.body, { childList: true, subtree: true })
}

function stopObserve(): void {
  if (mo) { mo.disconnect(); mo = null }
  window.clearTimeout(pinScanTimer)
}

/** 移除全部图标（关闭时回到完全干净的状态）。 */
function removePins(): void {
  pinEls.forEach((bt) => bt.remove())
  pinEls.clear()
  // 同时清掉"已注入"记录，否则下次开启时 pinNode 会把所有节点当成已处理而跳过
  pinned = new WeakSet<HTMLElement>()
}

function flashPin(bt: HTMLElement): void {
  bt.classList.add('dm-pin-ok')
  window.setTimeout(() => bt.classList.remove('dm-pin-ok'), 1000)
}

/* --------------------------------------------------- 提示层（仅悬停图标/Alt 时） */

function ensureHost(): void {
  if (hostEl) return
  hostEl = document.createElement('div')
  hostEl.id = 'devmodeRoot'
  boxEl = document.createElement('div')
  boxEl.className = 'dm-box'
  hintEl = document.createElement('div')
  hintEl.className = 'dm-hint'
  // 右下角常驻徽标已彻底移除——开关在顶栏按钮上，板块内 ⧉ 小图标才是「点一下复制」的主交互。
  hostEl.append(boxEl, hintEl)
  document.body.appendChild(hostEl)
}

/** 渲染提示气泡内容（code + 锚点角色 + 复制按钮）。 */
function renderHint(hint: Hint, withCopyBtn: boolean): void {
  ensureHost()
  if (!hintEl) return
  hintEl.textContent = ''
  const textWrap = document.createElement('div')
  textWrap.className = 'dm-text'
  const codeLine = document.createElement('span')
  codeLine.className = 'dm-code'
  codeLine.textContent = hint.code
  textWrap.appendChild(codeLine)
  const metaLine = document.createElement('span')
  metaLine.className = 'dm-file'
  metaLine.textContent = hint.role ? `${hint.base} · ${hint.role}` : hint.base
  textWrap.appendChild(metaLine)
  hintEl.appendChild(textWrap)

  if (withCopyBtn) {
    const copyBtn = document.createElement('button')
    copyBtn.type = 'button'
    copyBtn.className = 'dm-copy'
    copyBtn.title = `复制 code：${hint.code}`
    copyBtn.setAttribute('aria-label', `复制 ${hint.code}`)
    copyBtn.appendChild(makeIcon())
    copyBtn.dataset.code = hint.code
    hintEl.appendChild(copyBtn)
  } else {
    const t = document.createElement('span')
    t.className = 'dm-tip'
    t.textContent = 'Alt 细分'
    hintEl.appendChild(t)
  }
  hintEl.dataset.code = hint.code
}

/** 在图标旁边显示气泡（不追鼠标：位置由图标决定）。 */
function showPinTip(bt: HTMLElement, hint: Hint): void {
  renderHint(hint, true)
  if (!hintEl || !boxEl) return
  boxEl.style.display = 'none'
  hintEl.style.display = 'flex'
  const r = bt.getBoundingClientRect()
  hintEl.style.visibility = 'hidden'
  const w = hintEl.offsetWidth || 260
  const h = hintEl.offsetHeight || 48
  // 优先贴图标右侧；右侧放不下贴左侧；都按视口夹紧（不越界、不盖住图标本身）
  let left = r.right + 8
  if (left + w > window.innerWidth - 8) left = Math.max(8, r.left - w - 8)
  let top = r.top
  if (top + h > window.innerHeight - 8) top = Math.max(8, window.innerHeight - h - 8)
  hintEl.style.left = `${Math.round(left)}px`
  hintEl.style.top = `${Math.round(top)}px`
  hintEl.style.visibility = ''
}

/** Alt 细粒度模式：跟随悬停的任意元素显示 code（用户按住了 Alt，属显式意图）。 */
function showFollowTip(hint: Hint): void {
  renderHint(hint, false)
  if (!hintEl || !boxEl) return
  hintEl.style.display = 'flex'
  const r = hint.target.getBoundingClientRect()
  boxEl.style.display = 'block'
  boxEl.style.left = `${Math.round(r.left)}px`
  boxEl.style.top = `${Math.round(r.top)}px`
  boxEl.style.width = `${Math.round(r.width)}px`
  boxEl.style.height = `${Math.round(r.height)}px`

  hintEl.style.visibility = 'hidden'
  const w = hintEl.offsetWidth || 260
  const h = hintEl.offsetHeight || 48
  // 放在元素「外框的右上方」，避开元素本体所在区域（不挡内容）
  let left = r.right + 10
  if (left + w > window.innerWidth - 8) left = Math.max(8, r.left - w - 10)
  let top = r.top - h - 6
  if (top < 8) top = Math.min(r.bottom + 6, window.innerHeight - h - 8)
  hintEl.style.left = `${Math.round(left)}px`
  hintEl.style.top = `${Math.round(top)}px`
  hintEl.style.visibility = ''
}

function hideHint(): void {
  window.clearTimeout(hideTimer)
  current = null
  if (boxEl) boxEl.style.display = 'none'
  if (hintEl) hintEl.style.display = 'none'
}

/** 延迟隐藏：鼠标从图标/元素移向气泡时会短暂离开。立即隐藏会「点不到」——
 *  悬停类交互最常见的坑，必须留宽限期；进入气泡或图标时 cancelHide 撤销。 */
function scheduleHide(): void {
  window.clearTimeout(hideTimer)
  hideTimer = window.setTimeout(hideHint, HIDE_GRACE_MS)
}

function cancelHide(): void {
  window.clearTimeout(hideTimer)
}

/* ------------------------------------------------------------------- 事件处理 */

/** Alt 细粒度：悬停任意元素 → 跟随提示（只在按住 Alt 时生效）。
 *  不按 Alt 时页面完全安静，只有固定图标，不干扰正常使用。 */
function onPointerMove(e: PointerEvent | MouseEvent): void {
  if (!enabled) return
  if (!altHeld) {
    // 没按 Alt：只在鼠标进入「气泡自身」时保活，其余不干预
    const t = e.target as HTMLElement | null
    if (t && t.closest('#devmodeRoot')) cancelHide()
    return
  }
  const target = e.target as HTMLElement | null
  if (!target || !target.isConnected) return scheduleHide()
  if (target.closest('#devmodeRoot')) return cancelHide()
  // 图标本身也带 data-dev 祖先，交给图标自己的 hover 处理，避免两个气泡打架
  if (target.closest('.' + PIN_CLASS)) return
  if (current?.target === target) return cancelHide()
  const hint = resolveHint(target)
  if (!hint) return scheduleHide()
  cancelHide()
  current = hint
  showFollowTip(hint)
}

function onMouseMove(e: MouseEvent): void {
  onPointerMove(e)
}

function onClick(e: MouseEvent): void {
  if (!enabled) return
  const target = e.target as HTMLElement | null
  // 只处理「气泡上的复制按钮」（Alt 模式下的气泡）。固定图标有各自的 listener。
  const btn = target?.closest('.dm-copy') as HTMLElement | null
  if (!btn) return
  e.preventDefault()
  e.stopPropagation()
  const code = btn.dataset.code || ''
  void copy(code)
  flashCopied(btn, code)
}

async function copy(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text)
    return
  } catch { /* 非 https / 无权限时退回 execCommand */ }
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    document.execCommand('copy')
    ta.remove()
  } catch { /* 复制失败不打断使用 */ }
}

function flashCopied(el: HTMLElement, code: string): void {
  window.clearTimeout(copyTimer)
  const hint = el.closest('.dm-hint')
  if (hint) hint.classList.add('dm-copied')
  const tip = hint?.querySelector('.dm-tip')
  if (tip) tip.textContent = `已复制 ${code}`
  copyTimer = window.setTimeout(() => {
    if (hint) hint.classList.remove('dm-copied')
    if (tip) tip.textContent = 'Alt 细分'
  }, 1400)
}

function onKeydown(e: KeyboardEvent): void {
  if (e.ctrlKey && e.shiftKey && !e.altKey && e.key.toLowerCase() === TOGGLE_KEYS.key) {
    e.preventDefault()
    setEnabled(!enabled, true)
    return
  }
  if (e.key === 'Alt' && !altHeld) {
    altHeld = true
    emit()          // 顶栏按钮据此显示「细粒度」提示
  }
}

function onKeyup(e: KeyboardEvent): void {
  if (e.key === 'Alt') {
    altHeld = false
    emit()
    hideHint()
  }
}

/** 失焦兜底：切窗口时 keyup 收不到，Alt 状态会卡住。 */
function onBlur(): void {
  altHeld = false
  emit()
  hideHint()
}

/* --------------------------------------------------------------------- 开关 */

/**
 * 唯一的开关实现——所有入口（顶栏按钮 / Ctrl+Shift+D / 启动自动开）都必须走这里。
 *
 * ★ 环境守卫（0.4.70 补，本项目同源）：**开启**要求后端已判定为 local。
 * 关闭在任何环境都放行（免得卡死在开启态）。
 * 为什么必须有这道守卫：`[data-dev]` 节点在生产构建里**真实存在**（源码里就写着），
 * 所以一旦在生产被开启，图标就会挂到线上页面、把内部 code 暴露给真实用户。
 * 此前快捷键分支没有校验，属于实际存在的口子，这次一并堵上。
 */
function setEnabled(next: boolean, byUser = false): void {
  if (next === enabled) return
  if (next && !isLocalEnv) {
    if (!envChecked) {
      // 还没探测过：先探一次再决定（本地首屏加载中抢先按快捷键的常见情形）。
      // detectLocalEnv 结论会缓存，重复调用不会反复打接口。
      void detectLocalEnv().then((ok) => { if (ok) setEnabled(true, byUser) })
      return
    }
    // 已明确判定为非本地/测试环境 → 硬拒绝，且不留任何痕迹（不注入、不写 storage）
    console.warn('[devmode] 当前不是本地/测试环境，已忽略开启请求（开发模式生产绝不显示）')
    return
  }
  enabled = next
  if (byUser) {
    try { localStorage.setItem(STORAGE_KEY, next ? '1' : '0') } catch { /* 无痕模式忽略 */ }
  }
  if (next) {
    ensureHost()                       // 立刻建覆盖层（气泡容器）
    document.documentElement.classList.add('dm-on')
    scanAndPin()                       // 注入固定图标（主交互）
    startObserve()                     // 跟进异步渲染出的新板块
    document.addEventListener('pointermove', onPointerMove, true)
    document.addEventListener('mousemove', onMouseMove, true)
    document.addEventListener('click', onClick, true)
  } else {
    document.documentElement.classList.remove('dm-on')
    document.removeEventListener('pointermove', onPointerMove, true)
    document.removeEventListener('mousemove', onMouseMove, true)
    document.removeEventListener('click', onClick, true)
    stopObserve()
    removePins()
    window.clearTimeout(hideTimer)
    hideHint()
  }
  emit()            // 通知顶栏按钮更新高亮/文案
}

/**
 * 入口。仅在本地/测试环境（后端 env==='local'）才可能生效；
 * 用户手动关过就尊重用户的关闭（localStorage），之后可用 Ctrl+Shift+D 再开。
 *
 * 探测带重试：看板启动初期接口可能瞬时不可用（连不上/还没起来），
 * 单次失败就永久不开会让开发模式时好时坏，所以失败后间隔重试若干次。
 */
export async function initDevMode(): Promise<void> {
  let stored: string | null = null
  try { stored = localStorage.getItem(STORAGE_KEY) } catch { /* 无痕模式忽略 */ }
  const userWants = stored !== '0'      // 默认开；只有明确关过才不开

  // 先给快捷键/Alt 上监听：任何时候都能手动开/关，生产环境下按了也不会有可见效果
  document.addEventListener('keydown', onKeydown)
  document.addEventListener('keyup', onKeyup)
  window.addEventListener('blur', onBlur)

  for (const delay of [0, 1500, 4000, 10000]) {
    if (delay) await new Promise((r) => window.setTimeout(r, delay))
    if (await detectLocalEnv()) {
      // ★ 探测到本地/测试环境就返回——**即使不自动开启也要走到这里**。
      // 因为顶栏那个开发模式开关靠 local 状态渲染：
      // 用户上次手动关过开发模式时，按钮仍必须出现在顶栏，否则就再也开不回来了。
      if (userWants && localStorage.getItem(STORAGE_KEY) !== '0') setEnabled(true)
      return
    }
    // 非本地环境 + 用户主动关过：不必再重试（生产本来就不该开）
    if (!userWants) return
  }
}

/** 测试用：复位到「未探测、未开启」的初始态，避免用例之间互相污染。 */
export function __resetForTest(): void {
  setEnabled(false)
  envChecked = false
  isLocalEnv = false
  altHeld = false
  current = null
}
